import React, { useState } from 'react'
import { sendNewCustomerOTP, verifyNewCustomerOTP } from '../../../../api-utils'

interface OtpVerificationStepProps {
  firstName: string
  lastName: string
  email: string
  phone: string
  onVerified: (customerId: string, token: string, status?: string) => void
  onBack: () => void
  onError: (message: string) => void
  /** Override the OTP send. Returns { success, otpId?, error? }. Defaults to the new-customer OTP send. */
  sendOtp?: (email: string) => Promise<{ success: boolean; otpId?: string; error?: string }>
  /** Override the OTP verify. Returns { success, token?, customerId?, status?, error? }. Defaults to the new-customer OTP verify. */
  verifyOtp?: (otpId: string, otpCode: string) => Promise<any>
}

const OtpVerificationStep: React.FC<OtpVerificationStepProps> = ({
  firstName,
  lastName,
  email,
  phone,
  onVerified,
  onBack,
  onError,
  sendOtp,
  verifyOtp,
}) => {
  const [otpId, setOtpId] = useState<string | null>(null)
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [sent, setSent] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  const defaultSendOtp = (em: string) =>
    sendNewCustomerOTP({ first_name: firstName, last_name: lastName, email: em, phone })
  const effectiveSend = sendOtp ?? defaultSendOtp

  const defaultVerifyOtp = (id: string, code: string) =>
    verifyNewCustomerOTP(id, code, customerId!)
  const effectiveVerify = verifyOtp ?? defaultVerifyOtp

  const handleSendOtp = async () => {
    setIsSending(true)
    setVerifyError(null)
    try {
      const response = await effectiveSend(email)
      if (response.success) {
        setOtpId(response.otpId ?? null)
        if (!sendOtp) {
          // default path: sendNewCustomerOTP also returns customerId
          setCustomerId((response as any).customerId ?? null)
        }
        setSent(true)
      } else if ((response as any).existingCustomer) {
        onError('An account with this email already exists. Please use the "Already a customer?" login.')
      } else {
        onError(response.error || 'Failed to send verification code')
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Failed to send verification code'
      if (error.response?.data?.existingCustomer) {
        onError('An account with this email already exists. Please use the "Already a customer?" login.')
      } else {
        onError(msg)
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otpId || (!sendOtp && !customerId) || !otpCode) return
    setIsVerifying(true)
    setVerifyError(null)
    try {
      const response = await effectiveVerify(otpId, otpCode)
      if (response.success) {
        const resolvedCustomerId = sendOtp ? (response.customerId ?? '') : customerId!
        onVerified(resolvedCustomerId, response.token, response.status)
      } else {
        setVerifyError(response.error || 'Verification failed')
      }
    } catch (error: any) {
      setVerifyError(error.response?.data?.error || 'Invalid or expired code. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {!sent ? (
        <>
          <div className="forceBlack" style={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
            <strong>Verify your email</strong>
            <p style={{ marginTop: '0.5rem' }}>
              We'll send a verification code to <strong>{email}</strong> to confirm your identity before continuing.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button
              type="button"
              onClick={onBack}
              style={{ background: 'transparent', color: '#232043', border: '2px solid #232043' }}
            >
              <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Back</span>
            </button>
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={isSending}
              style={{ background: '#232043', color: 'yellow' }}
            >
              <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>
                {isSending ? 'Sending...' : 'Send Verification Code'}
              </span>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="forceBlack" style={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
            <strong>Enter your verification code</strong>
            <p style={{ marginTop: '0.5rem' }}>
              We sent a 6-digit code to <strong>{email}</strong>. Check your inbox and enter it below.
            </p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="otp_code" className="forceBlack">Verification Code</label>
            <input
              id="otp_code"
              name="otp_code"
              placeholder="Enter 6-digit code"
              type="text"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              className={verifyError ? 'text-input error' : 'text-input'}
              style={{ fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' }}
              autoFocus
            />
            {verifyError && <div className="input-feedback">{verifyError}</div>}
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={onBack}
              style={{ background: 'transparent', color: '#232043', border: '2px solid #232043' }}
            >
              <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Back</span>
            </button>
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={isVerifying || otpCode.length !== 6}
              style={{ background: '#232043', color: 'yellow' }}
            >
              <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>
                {isVerifying ? 'Verifying...' : 'Verify & Continue'}
              </span>
            </button>
          </div>

          <div className="forceBlack" style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            Didn't receive the code?{' '}
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={isSending}
              style={{
                background: 'none', border: 'none', color: '#232043',
                textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '0.9rem'
              }}
            >
              {isSending ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default OtpVerificationStep
